import { useEffect, useState } from 'react';
import { haptic } from '../telegram';

export type ToastVariant = 'success' | 'info' | 'warning' | 'danger' | 'achievement';

export type Toast = {
  id: string;
  variant: ToastVariant;
  title: string;
  body?: string;
  durationMs?: number; // default 3000
};

const VARIANT_COLOR: Record<ToastVariant, { border: string; glow: string; icon: string }> = {
  success: { border: 'var(--fg-success)',  glow: '0 0 12px var(--fg-success)',  icon: '✓' },
  info:    { border: 'var(--fg-primary)',  glow: '0 0 12px var(--fg-primary)',  icon: 'ℹ' },
  warning: { border: 'var(--fg-warning)',  glow: '0 0 12px var(--fg-warning)',  icon: '⚠' },
  danger:  { border: 'var(--fg-danger)',   glow: '0 0 12px var(--fg-danger)',   icon: '✕' },
  achievement: { border: '#b76bff',          glow: '0 0 14px #b76bff',           icon: '★' },
};

const VARIANT_HAPTIC: Record<ToastVariant, () => void> = {
  success: () => haptic.notify('success'),
  info:    () => haptic.tick(),
  warning: () => haptic.notify('warning'),
  danger:  () => haptic.notify('error'),
  achievement: () => haptic.notify('success'),
};

const MAX_TOASTS = 3;

let _nextId = 0;
const listeners = new Set<(toasts: Toast[]) => void>();
let currentToasts: Toast[] = [];

function emit(): void {
  for (const fn of listeners) fn(currentToasts);
}

/** Show a new toast. Auto-dismisses after duration. Returns the toast id. */
export function pushToast(input: Omit<Toast, 'id'>): string {
  const id = `t${++_nextId}`;
  const toast: Toast = {
    id,
    durationMs: 3000,
    ...input,
  };
  currentToasts = [...currentToasts, toast].slice(-MAX_TOASTS);
  VARIANT_HAPTIC[toast.variant]();
  emit();
  setTimeout(() => {
    dismissToast(id);
  }, toast.durationMs ?? 3000);
  return id;
}

/** Dismiss a toast by id (or no-op if already gone). */
export function dismissToast(id: string): void {
  const next = currentToasts.filter((t) => t.id !== id);
  if (next.length === currentToasts.length) return;
  currentToasts = next;
  emit();
}

/** React hook: returns the current toast list, re-rendering on changes. */
function useToasts(): Toast[] {
  const [toasts, setToasts] = useState<Toast[]>(currentToasts);
  useEffect(() => {
    const fn = (list: Toast[]) => setToasts(list);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return toasts;
}

/** Convenience wrapper: push an "achievement" toast with star/purple styling. */
export function fireAchievement(title: string, body?: string, durationMs = 3500): string {
  return pushToast({ variant: 'achievement', title, body, durationMs });
}

/**
 * Mount this once at the App root. Renders the stack of active toasts.
 */
export function ToastContainer() {
  const toasts = useToasts();

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + var(--space-4))',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        zIndex: 250,
        pointerEvents: 'none',
        maxWidth: 'calc(100vw - 2rem)',
        width: 'min(22rem, calc(100vw - 2rem))',
      }}
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => {
        const v = VARIANT_COLOR[t.variant];
        return (
          <div
            key={t.id}
            onClick={() => dismissToast(t.id)}
            className="slide-in-up"
            style={{
              pointerEvents: 'auto',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
              padding: 'var(--space-3)',
              background: 'var(--bg-overlay)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: `1px solid ${v.border}`,
              boxShadow: v.glow,
              animationDuration: '0.25s',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                color: v.border,
                fontSize: '1.2rem',
                lineHeight: 1,
                marginTop: 1,
                filter: `drop-shadow(0 0 4px ${v.border})`,
              }}
            >
              {v.icon}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
              <strong style={{
                color: v.border,
                fontSize: '0.78rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}>
                {t.title}
              </strong>
              {t.body && (
                <span style={{
                  fontSize: '0.7rem',
                  color: 'var(--fg-text-dim)',
                  lineHeight: 1.35,
                }}>
                  {t.body}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
